import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";


interface VendorOption {
    code?: string;
    name?: string;
    phone?: string;
}

interface VendorAutocompleteProps<T = VendorOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: VendorOption) => T;

    [key: string]: any;
}


const VendorAutocomplete = <T = VendorOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                                   textFieldProps = {},
                                                                                                                                   form = false,
                                                                                                                                   name = undefined,
                                                                                                                                   required = false,
                                                                                                                                   setLoadingOptions: setLoadingOptionsProps,
                                                                                                                                   transformRecord,
                                                                                                                                   ...props
                                                                                                                               }: VendorAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches vendor options based on search keywords
     * Implements pagination and keyword filtering
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching vendor options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.listVendorsV2({keyword: keywords, pagination: {currentPage: 1, pageSize: 20}, sorts: []}, cancelToken);
            if (!isMounted())
                return [];
            return response?.data?.items?.map(e => transformRecord?.(e) ?? e as VendorOption) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, transformRecord]);

    /**
     * Determines if two vendor options are equal based on their codes
     */
    const isOptionEqualToValue = useCallback((option: VendorOption, value: VendorOption | null) => option?.code === value?.code, []);

    /**
     * Gets the display label for a vendor option (vendor code)
     */
    const getOptionLabel = useCallback((option: VendorOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : (option as VendorOption)?.code ?? "", [props.freeSolo]);

    /**
     * Gets the unique key for a vendor option (vendor code)
     */
    const getOptionKey = useCallback((option: VendorOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : (option as VendorOption)?.code ?? "", [props.freeSolo]);

    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            {...props}
        />
    );
};

export default VendorAutocomplete;
